import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import apiClient from '../config/api';
import { getApplications, isCustomerActivationEnabled } from '../services/coreService';

const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [hasPermissions, setHasPermissions] = useState(false);
    const [userRoles, setUserRoles] = useState([]);
    const [authError, setAuthError] = useState(null);
    const [permissionError, setPermissionError] = useState(null);
    const [applicationModules, setApplicationModules] = useState([]);
    const [customerActivationEnabled, setCustomerActivationEnabled] = useState(false);
    const isCheckingAuth = useRef(false);

    const checkPermissions = useCallback(async () => {
        try {
            setPermissionError(null); // Clear any previous errors
            const response = await apiClient.get('/api/auth/permissions');
            
            if (response.data.hasPermissions) {
                setHasPermissions(true);
                setUserRoles(response.data.roles);
                setPermissionError(null);
                return { hasPermissions: true, error: null };
            } else {
                // User has no permissions - this is a legitimate case
                setHasPermissions(false);
                setUserRoles([]);
                setPermissionError(null);
                return { hasPermissions: false, error: null }; // This will trigger redirect to permission denied page
            }
        } catch (error) {
            console.error('Error checking permissions:', error);
            
            // Set the error for display
            setPermissionError(error);
            
            // Check if it's a server error (500) or client error (400)
            if (error.response && error.response.status >= 500) {
                console.error('Server error during permission check:', error.response.data);
                setHasPermissions(false);
                setUserRoles([]);
                return { hasPermissions: false, error: error }; // Don't redirect on server errors
            } else if (error.response && error.response.status === 400) {
                console.error('Client error during permission check:', error.response.data);
                setHasPermissions(false);
                setUserRoles([]);
                return { hasPermissions: false, error: error }; // Don't redirect on client errors
            } else if (error.response && error.response.status === 429) {
                console.error('Rate limited during permission check:', error.response.data);
                setHasPermissions(false);
                setUserRoles([]);
                return { hasPermissions: false, error: error }; // Don't redirect on rate limit errors
            } else {
                // Network or other errors
                setHasPermissions(false);
                setUserRoles([]);
                return { hasPermissions: false, error: error }; // Don't redirect on network errors
            }
        }
    }, []); // Empty dependency array since this function doesn't depend on any props or state

    const fetchApplicationModules = useCallback(async () => {
        try {
            const modules = await getApplications();
            setApplicationModules(modules);
            
            // Check if customer activation is enabled for Application ID 1
            const isActivationEnabled = await isCustomerActivationEnabled(1);
            setCustomerActivationEnabled(isActivationEnabled);
            
            return modules;
        } catch (error) {
            console.error('Error fetching application modules:', error);
            setApplicationModules([]);
            setCustomerActivationEnabled(false);
            return [];
        }
    }, []);

    const checkAuthStatus = useCallback(async (retries = 2) => {
        // Prevent multiple simultaneous auth checks
        if (isCheckingAuth.current) {
            // Wait for the current check to complete
            let attempts = 0;
            while (isCheckingAuth.current && attempts < 50) { // Max 5 seconds wait
                await new Promise(resolve => setTimeout(resolve, 100));
                attempts++;
            }
            // Return the current state after waiting
            return isAuthenticated && hasPermissions;
        }
        isCheckingAuth.current = true;
        
        try {
            for (let attempt = 1; attempt <= retries; attempt++) {
                try {
                    const response = await apiClient.get('/api/auth/status');
                    
                        if (response.data.isAuthenticated) {
                        setUser(response.data.user);
                        setIsAuthenticated(true);
                        
                        // Check permissions after successful authentication
                        const permissionResult = await checkPermissions();
                        
                        if (!permissionResult.hasPermissions) {
                            // Check if this is due to an error or actual lack of permissions
                            if (permissionResult.error) {
                                // There was an error during permission check - don't redirect
                                setLoading(false);
                                setHasPermissions(false);
                                return false;
                            } else {
                                // User legitimately has no permissions
                                setHasPermissions(false);
                                setLoading(false);
                                return false;
                            }
                        } else {
                            // Explicitly set the state here to ensure it's set before ProtectedRoute checks
                            setHasPermissions(true);
                            
                            // Fetch application modules after successful authentication
                            await fetchApplicationModules();
                            
                            setLoading(false);
                            return true; // Success
                        }
                    } else {
                        setUser(null);
                        setIsAuthenticated(false);
                        setHasPermissions(false);
                        setUserRoles([]);
                        console.log('User not authenticated');
                        setLoading(false);
                        return false; // Not authenticated
                    }
                } catch (error) {
                    console.error(`Error checking auth status (attempt ${attempt}):`, error);
                    
                    // Check if it's a rate limit error
                    if (error.response && error.response.status === 429) {
                        console.log('Rate limit exceeded, waiting longer before retry...');
                        if (attempt === retries) {
                            setUser(null);
                            setIsAuthenticated(false);
                            setHasPermissions(false);
                            setUserRoles([]);
                            setLoading(false);
                            return false;
                        } else {
                            // Wait longer for rate limit
                            await new Promise(resolve => setTimeout(resolve, 5000));
                        }
                    } else if (attempt === retries) {
                        // Final attempt failed
                        setUser(null);
                        setIsAuthenticated(false);
                        setHasPermissions(false);
                        setUserRoles([]);
                        setLoading(false);
                        return false;
                    } else {
                        // Wait before retry
                        await new Promise(resolve => setTimeout(resolve, 2000));
                    }
                }
            }
        } finally {
            isCheckingAuth.current = false;
        }
        
        setLoading(false);
        return false;
    }, []); // Empty dependency array since this function doesn't depend on any props or state

    // Check authentication status on mount
    const hasInitialized = useRef(false);
    useEffect(() => {
        // Only run once on mount
        if (hasInitialized.current) return;
        hasInitialized.current = true;
        
        // Don't check auth status if we're on pages that don't need authentication
        const skipAuthPages = ['/permission-denied', '/login', '/customer-form', '/activation-success'];
        const currentPath = window.location.pathname;
        
        if (skipAuthPages.includes(currentPath)) {
            setLoading(false);
            setUser(null);
            setIsAuthenticated(false);
            setHasPermissions(false);
            setUserRoles([]);
            return;
        }
        
        // Don't auto-check auth status on callback page - let AuthCallback handle it
        if (currentPath === '/auth/callback') {
            setLoading(false);
            return;
        }
        checkAuthStatus();
    }, []); // Empty dependency array - only run once on mount

    const login = (redirectUrl = null) => {
        // Store redirect URL in sessionStorage for retrieval after OAuth
        if (redirectUrl) {
            console.log('Storing redirect URL:', redirectUrl);
            sessionStorage.setItem('loginRedirect', redirectUrl);
        }
        
        // Redirect to Google OAuth
        window.location.href = `${apiClient.defaults.baseURL}/api/auth/google`;
    };

    const logout = async () => {
        try {
            await apiClient.post('/api/auth/logout');
            setUser(null);
            setIsAuthenticated(false);
            setHasPermissions(false);
            setUserRoles([]);
            
            // Clear any cached Google OAuth session by redirecting to Google logout
            // This forces the user to choose an account again
            const googleLogoutUrl = 'https://accounts.google.com/logout';
            window.open(googleLogoutUrl, '_blank');
            
            // Small delay to ensure Google logout completes, then redirect to login
            setTimeout(() => {
                window.location.href = '/login';
            }, 1000);
        } catch (error) {
            console.error('Error logging out:', error);
            // Even if the logout request fails, clear local state
            setUser(null);
            setIsAuthenticated(false);
            setHasPermissions(false);
            setUserRoles([]);
            
            // Still try to clear Google session
            const googleLogoutUrl = 'https://accounts.google.com/logout';
            window.open(googleLogoutUrl, '_blank');
            
            setTimeout(() => {
                window.location.href = '/login';
            }, 1000);
        }
    };

    const value = {
        user,
        isAuthenticated,
        loading,
        hasPermissions,
        userRoles,
        authError,
        permissionError,
        applicationModules,
        customerActivationEnabled,
        login,
        logout,
        checkAuthStatus,
        checkPermissions,
        fetchApplicationModules
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
